<template>
  <el-dropdown
    trigger="click"
    style="height: 21px;"
    @command="handleAttendanceCommand"
  >
    <span class="el-dropdown-link">
      <div class="more-action__button ic-more" />
    </span>
    <el-dropdown-menu
      slot="dropdown"
      class="attendance-dropdown"
    >
      <el-dropdown-item
        v-for="(dropdownItem, index) in dropdownItems"
        :key="index"
        :class="dropdownItem.statusClass"
        :command="dropdownItem.command"
        :disabled="presentIsAttendanceDisabled(dropdownItem)"
      >
        {{ dropdownItem.name }}
      </el-dropdown-item>
      <el-dropdown-item
        v-if="enrollmentOption"
        :command="enrollmentOptionItem"
        divided
      >
        회원 페이지로 이동
      </el-dropdown-item>
    </el-dropdown-menu>
  </el-dropdown>
</template>

<script>
import _ from 'lodash';
import { addMinutes, isAfter } from 'date-fns';
import { getBookingStatus } from '../utils/booking-status';
import { mapGetters } from 'vuex';
import { fiflboxApi } from '@/utils/api';

const BOOKING_STATUS = {
  NOSHOW: 'NOSHOW',
  ABSENCE: 'ABSENCE',
  ATTENDANCE: 'ATTENDANCE',
  LATE: 'LATE',
  BOOKED: 'BOOKED',
  CANCELLED: 'CANCELLED'
};

const BOOKING_STATUS_TO_KOR = {
  NOSHOW: '노쇼',
  ABSENCE: '결석',
  ATTENDANCE: '출석',
  LATE: '지각',
  BOOKED: '예약',
  CANCELLED: '취소'
};

const dropdownMenus = [
  {
    name: '예약중',
    visibleInBookingSystem: true,
    visibleInNonBookingSystem: false,
    alert: '예약 상태로 변경되었습니다.',
    command: {
      action: BOOKING_STATUS.BOOKED
    }
  },
  {
    name: '예약 취소',
    visibleInBookingSystem: true,
    visibleInNonBookingSystem: false,
    alert: '예약이 취소됐습니다.',
    command: {
      action: BOOKING_STATUS.CANCELLED
    }
  },
  {
    name: '출석 처리',
    visibleInBookingSystem: true,
    visibleInNonBookingSystem: true,
    alert: '출석이 완료됐습니다.',
    command: {
      action: BOOKING_STATUS.ATTENDANCE
    }
  },
  {
    name: '지각 처리',
    visibleInBookingSystem: true,
    visibleInNonBookingSystem: true,
    alert: '지각 처리됐습니다.',
    command: {
      action: BOOKING_STATUS.LATE
    }
  },
  {
    name: '결석 처리',
    visibleInBookingSystem: true,
    visibleInNonBookingSystem: true,
    alert: '결석 처리됐습니다.',
    command: {
      action: BOOKING_STATUS.ABSENCE
    }
  },
  {
    name: '출석 취소',
    visibleInBookingSystem: false,
    visibleInNonBookingSystem: true,
    alert: '출석이 취소됐습니다.',
    command: {
      action: BOOKING_STATUS.CANCELLED
    }
  }
];

export default {
  name: 'AttendanceControlDropdown',
  props: {
    data: {},
    courseStart: {},
    enrollmentOption: {
      default: false,
      type: Boolean
    },
    disabled: {
      default: false,
      type: Boolean
    },
    memberId: {}
  },
  data() {
    return {
      enrollmentOptionItem: {
        action: 'GO_ENROLLMENT'
      }
    };
  },
  computed: {
    ...mapGetters({
      centerAccount: 'getCenterAccount',
      policy: 'getPolicy'
    }),
    dropdownMenus() {
      return dropdownMenus.filter(menu => {
        if (this.isBookingSystem) {
          return menu.visibleInBookingSystem;
        }

        return menu.visibleInNonBookingSystem;
      });
    },
    dropdownItems() {
      return this.dropdownMenus.map(menu => {
        return {
          statusClass:
            this.curState === menu.command.action ? 'current-status' : '',
          ...menu
        };
      });
    },
    curState() {
      return getBookingStatus(this.data);
    },
    isBookingSystem() {
      return this.$store.getters.getIsBookingSystem;
    }
  },
  methods: {
    presentIsAttendanceDisabled({ command }) {
      if (this.disabled) {
        return true;
      }

      if (command.action !== BOOKING_STATUS.BOOKED) {
        return false;
      }

      const lateTime = this.policy.late_from;
      const isAfterCourseTime = isAfter(
        addMinutes(this.data.dt, lateTime),
        new Date()
      );

      return !isAfterCourseTime;
    },
    makeBookingForm(status) {
      let form = _.cloneDeep(this.data);
      form.status = status;

      if (form.child_course.id) {
        form.child_course = form.child_course.id;
      }

      if (form.child_membership.id) {
        form.child_membership = form.child_membership.id;
      }

      return form;
    },
    async cancelBooking() {
      let form = this.makeBookingForm(BOOKING_STATUS.CANCELLED);

      try {
        await fiflboxApi.v1.putBookings(form.id, form);
        return true;
      } catch (err) {
        return false;
      }
    },
    async updateBookingToBookedStatus() {
      let form = this.makeBookingForm(BOOKING_STATUS.BOOKED);
      form.attendance.status = BOOKING_STATUS.NOSHOW;

      await fiflboxApi.v1.putBookingAttendances(
        form.attendance.id,
        form.attendance
      );
    },
    async updateAttendance(action) {
      let form = _.cloneDeep(this.data.attendance);
      form.status = action;

      await fiflboxApi.v1.putBookingAttendances(form.id, form);
    },
    async handleAttendanceCommand({ action }) {
      if (action === 'GO_ENROLLMENT') {
        return this.$router.push('/member/' + this.memberId).catch(() => {});
      }

      if (action === BOOKING_STATUS.CANCELLED) {
        const result = await this.cancelBooking();

        if (!result) {
          await this.$message(
            `이미 ${BOOKING_STATUS_TO_KOR[action]}된 예약입니다.`
          );
          return this.$emit('update', {});
        }
      }

      if (action === BOOKING_STATUS.BOOKED) {
        await this.updateBookingToBookedStatus();
      }

      if (
        action !== BOOKING_STATUS.CANCELLED &&
        action !== BOOKING_STATUS.BOOKED
      ) {
        await this.updateAttendance(action);
      }

      let menu = this.dropdownMenus.find(
        menu => menu.command.action === action
      );

      this.$message(menu.alert);

      return this.$emit('update', {});
    }
  }
};
</script>

<style lang="scss">
.attendance-dropdown {
  min-width: 90px;

  .el-dropdown-menu__item {
    list-style: none;
    line-height: 36px;
    padding: 0 20px;
    margin: 0;
    font-size: 14px;
    color: #9b9b9b;
    cursor: pointer;
    outline: none;

    &.current-status {
      font-weight: 500;
      color: #4a4a4a;
    }

    &.is-disabled {
      cursor: not-allowed;
      color: rgba(200, 200, 200, 0.5);
      pointer-events: none;
    }
  }
}
</style>
